import "./styles.scss";

import { useTranslation } from "react-i18next";
import { IconArrowLeft } from "@tabler/icons-react";

import { AUTOPLAY_COUNTDOWN_SECONDS } from "../hooks/useAutoplayCountdown";

import ControlBar from "./components/ControlBar";
import ControlButton from "./components/ControlButton";
import ProgressBar from "./components/ProgressBar";
import AutoplayCountdown from "./components/AutoplayCountdown";
import AmbientGlow from "./components/AmbientGlow";
import PlayerFlash from "./components/PlayerFlash";
import { AdminPreviewBadge } from "./components/Banners";

function handleBack() {
    if (typeof window.navigate === "function") window.navigate(-1);
    else window.history.back();
}

/**
 * PlayerView — pure rendering layer of the custom player.
 *
 * Landscape (`mode="horizontal"`): controls overlay the bottom of the video.
 * Portrait  (`mode="vertical"`):   only the progress slider stays inside the
 * video; the control rail sits outside, to the right of the stage.
 *
 * `disabled` (no video source): the chrome renders as usual but every
 * playback control is inert, and `placeholder` — the creator's "no video
 * yet" card — floats in the middle of the stage.
 *
 * `embedded` (inside a modal): the modal owns closing, so there is no back
 * button, and theater mode (a page layout) is left out of the bar.
 */
export default function PlayerView({
    info,
    mode,
    videoRef,
    rootRef,
    controls,
    captions,
    watched,
    onToggleWatched,
    prevContent,
    nextContent,
    onNavigate,
    autoplay,
    isAdminPreview,
    needsPlayGesture,
    disabled = false,
    placeholder = null,
    embedded = false,
}) {
    const { t } = useTranslation("content");
    const { state, actions } = controls;
    const controlsHidden = state.isRest && state.isPlaying;

    const controlBar = (
        <ControlBar
            videoRef={videoRef}
            state={state}
            actions={actions}
            captions={captions}
            watched={watched}
            onToggleWatched={onToggleWatched}
            prevContent={prevContent}
            nextContent={nextContent}
            onNavigate={onNavigate}
            autoplayEnabled={autoplay.enabled}
            onAutoplayChange={autoplay.setEnabled}
            single={!!info?.single}
            vertical={mode === "vertical"}
            disabled={disabled}
            embedded={embedded}
        />
    );

    return (
        <div
            ref={rootRef}
            className={[
                "player",
                mode,
                controlsHidden ? "rest-mode" : "",
                state.isFullscreen ? "fullscreen" : "",
                disabled ? "no-video" : "",
                embedded ? "embedded" : "",
            ].join(" ").trim()}
            onPointerMove={actions.handleActivity}
            onPointerDown={actions.handleActivity}
            onPointerLeave={actions.handlePointerLeave}
        >
            {/* The glow is a theater-only effect: outside it, skipping the
                mount also spares the canvases/rAF work entirely. */}
            {state.isAmbient && state.isTheater && !disabled && (
                <AmbientGlow
                    videoRef={videoRef}
                    onPerformanceIssue={actions.disableAmbientForPerformance}
                />
            )}

            <div className="player-stage" onClick={actions.handleStageClick}>
                {/* No autoPlay attribute: the engine owns startup (resume seek
                    + unmuted play), and the attribute's own attempt races it. */}
                <video ref={videoRef} playsInline preload="auto" className="player-video" />

                <div className="player-top-bar">
                    {!embedded && (
                        <ControlButton label={t("common:actions.back")} onClick={handleBack} className="player-back-button">
                            <IconArrowLeft />
                        </ControlButton>
                    )}

                    {isAdminPreview && (
                        <AdminPreviewBadge
                            launchDate={info?.timestamps?.launch || info?.availability?.available_at}
                        />
                    )}
                </div>

                {/* Creator's "no video yet" card, centered over the stage. It
                    swallows its own clicks so they never reach the stage. */}
                {placeholder && (
                    <div className="player-placeholder" onClick={(event) => event.stopPropagation()}>
                        {placeholder}
                    </div>
                )}

                {/* Blocked unmuted autoplay: hold the play pulse on screen as
                    the click-to-play affordance (the tap lands on the stage,
                    which starts playback with audio). */}
                {needsPlayGesture ? (
                    <PlayerFlash flash={{ kind: "play" }} persistent />
                ) : (
                    state.flash && <PlayerFlash key={state.flash.id} flash={state.flash} />
                )}

                {autoplay.showCountdown && (
                    <AutoplayCountdown
                        videoRef={videoRef}
                        countdownSeconds={AUTOPLAY_COUNTDOWN_SECONDS}
                        onSkip={autoplay.trigger}
                        onCancel={autoplay.cancel}
                    />
                )}

                {/* Clicks on the bar (including pill gaps) must not reach the
                    stage's play/pause toggle. */}
                <div className="player-controls" onClick={(event) => event.stopPropagation()}>
                    <ProgressBar
                        videoRef={videoRef}
                        src={info?.video?.src}
                        aspectRatio={info?.video?.aspect_ratio}
                        disabled={disabled}
                    />
                    {mode === "horizontal" && controlBar}
                </div>
            </div>

            {mode === "vertical" && (
                <div className="player-rail">
                    {controlBar}
                </div>
            )}
        </div>
    );
}
